import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Award, Flame, Trophy, Crown, Target, Shield, Pill,
  Heart, Activity, Compass, BarChart3, TrendingUp, Star,
  Zap, Sprout, Layers, Share2, Copy, Check, X,
  MessageCircle, Twitter, ChevronRight, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  sprout: Sprout, flame: Flame, trophy: Trophy, crown: Crown,
  target: Target, shield: Shield, pill: Pill, layers: Layers,
  heart: Heart, activity: Activity, compass: Compass,
  'bar-chart': BarChart3, 'trending-up': TrendingUp, star: Star,
  zap: Zap, award: Award,
};

const CATEGORY_COLORS = {
  checkin: { bg: 'from-amber-500 to-orange-500', light: 'bg-amber-50 text-amber-700 border-amber-200' },
  stack: { bg: 'from-emerald-500 to-teal-500', light: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  wellness: { bg: 'from-violet-500 to-purple-500', light: 'bg-violet-50 text-violet-700 border-violet-200' },
  assessment: { bg: 'from-sky-500 to-blue-500', light: 'bg-sky-50 text-sky-700 border-sky-200' },
  progress: { bg: 'from-rose-500 to-pink-500', light: 'bg-rose-50 text-rose-700 border-rose-200' },
};

// ── Confetti particles ──
function Confetti() {
  const colors = ['#F59E0B', '#10B981', '#6366F1', '#EC4899', '#3B82F6', '#EF4444'];
  const particles = Array.from({ length: 40 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    delay: Math.random() * 0.5,
    duration: 1.5 + Math.random() * 1.5,
    color: colors[i % colors.length],
    size: 4 + Math.random() * 6,
    rotation: Math.random() * 360,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map(p => (
        <motion.div
          key={p.id}
          initial={{ y: -20, x: `${p.x}%`, opacity: 1, rotate: 0, scale: 1 }}
          animate={{ y: '120%', opacity: 0, rotate: p.rotation + 180, scale: 0.5 }}
          transition={{ duration: p.duration, delay: p.delay, ease: 'easeIn' }}
          className="absolute"
          style={{ width: p.size, height: p.size, backgroundColor: p.color, borderRadius: p.size > 7 ? '50%' : '2px' }}
        />
      ))}
    </div>
  );
}

// ── Celebration Modal ──
function CelebrationModal({ milestone, onClose, onShare }) {
  if (!milestone) return null;
  const Icon = ICON_MAP[milestone.icon] || Award;
  const colors = CATEGORY_COLORS[milestone.category] || CATEGORY_COLORS.checkin;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.5, opacity: 0, y: 40 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ type: 'spring', damping: 15, stiffness: 200 }}
        className="bg-white rounded-3xl max-w-sm w-full overflow-hidden relative"
        onClick={e => e.stopPropagation()}
        data-testid="celebration-modal"
      >
        <Confetti />

        {/* Gradient header */}
        <div className={`bg-gradient-to-r ${colors.bg} p-8 text-center relative`}>
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', delay: 0.2, damping: 10 }}
            className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-3"
          >
            <Icon className="w-10 h-10 text-white" />
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-2xl font-bold text-white"
          >
            Milestone Unlocked!
          </motion.h2>
        </div>

        <div className="p-6 text-center">
          <h3 className="text-xl font-bold text-[#1A1A1A] mb-1">{milestone.title}</h3>
          <p className="text-sm text-muted-foreground mb-5">{milestone.desc}</p>

          <div className="flex gap-2">
            <Button onClick={onShare} className="flex-1 btn-primary gap-2" data-testid="celebration-share-btn">
              <Share2 className="w-4 h-4" /> Share Achievement
            </Button>
            <Button variant="outline" onClick={onClose} className="px-4" data-testid="celebration-close-btn">
              Nice!
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── Shareable Milestones Card Preview ──
function MilestonesCardPreview({ data }) {
  if (!data) return null;
  return (
    <div
      className="rounded-2xl overflow-hidden w-full max-w-sm mx-auto"
      style={{ background: 'linear-gradient(145deg, #1E1B4B 0%, #312E81 100%)' }}
      data-testid="milestones-share-card"
    >
      <div className="px-6 pt-6 pb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-amber-500 flex items-center justify-center">
              <span className="text-white font-bold text-xs">V</span>
            </div>
            <span className="text-xs font-medium text-white/60">vitala.me</span>
          </div>
          <span className="text-[10px] text-white/40">{data.unlocked_count}/{data.total_count}</span>
        </div>

        <h2 className="text-lg font-bold text-white mb-1">{data.first_name}'s Achievements</h2>
        <p className="text-xs text-white/50 mb-4">{data.unlocked_count} milestones unlocked</p>

        <div className="grid grid-cols-4 gap-2">
          {data.unlocked.slice(0, 8).map(m => {
            const Icon = ICON_MAP[m.icon] || Award;
            const cat = CATEGORY_COLORS[m.category] || CATEGORY_COLORS.checkin;
            return (
              <div key={m.id} className="flex flex-col items-center gap-1 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.08)' }}>
                <Icon className="w-4 h-4 text-amber-400" />
                <span className="text-[8px] text-white/60 text-center leading-tight">{m.title.split(' ')[0]}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="px-6 py-3 border-t border-white/5 text-center">
        <p className="text-[10px] text-white/40">Start your wellness journey at</p>
        <p className="text-xs font-semibold text-amber-400">vitala.me</p>
      </div>
    </div>
  );
}

// ── Dashboard Milestones Widget ──
export function MilestonesWidget({ getAuthHeader }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [celebrating, setCelebrating] = useState(null);
  const [celebrationQueue, setCelebrationQueue] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchMilestones = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/milestones`, { headers: getAuthHeader() });
      setData(res.data);
      if (res.data.newly_unlocked?.length > 0) {
        setCelebrationQueue(res.data.newly_unlocked);
        setCelebrating(res.data.newly_unlocked[0]);
      }
    } catch (err) {
      console.error('Milestones fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchMilestones(); }, [fetchMilestones]);

  const handleCloseCelebration = async () => {
    const remaining = celebrationQueue.slice(1);
    if (remaining.length > 0) {
      setCelebrating(remaining[0]);
      setCelebrationQueue(remaining);
    } else {
      setCelebrating(null);
      setCelebrationQueue([]);
      // Acknowledge all
      try {
        await axios.post(`${API}/milestones/acknowledge`, {}, { headers: getAuthHeader() });
      } catch (err) {
        console.error('Ack error:', err);
      }
    }
  };

  const handleShare = async () => {
    setCelebrating(null);
    setCelebrationQueue([]);
    try {
      await axios.post(`${API}/milestones/acknowledge`, {}, { headers: getAuthHeader() });
    } catch (err) { /* ignore */ }

    setShowShareModal(true);
    try {
      const res = await axios.get(`${API}/milestones/share-card`, { headers: getAuthHeader() });
      setShareData(res.data);
    } catch (err) {
      toast.error('Failed to load share card');
      setShowShareModal(false);
    }
  };

  const handleCopyLink = () => {
    const link = shareData?.referral_link || 'https://vitala.me';
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareWhatsApp = () => {
    const link = shareData?.referral_link || 'https://vitala.me';
    const text = `I've unlocked ${shareData?.unlocked_count} wellness milestones on Vitala! Start your journey: ${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleShareTwitter = () => {
    const link = shareData?.referral_link || 'https://vitala.me';
    const text = `${shareData?.unlocked_count} wellness milestones unlocked on my supplement journey with @VitalaHealth! Start yours:`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(link)}`, '_blank');
  };

  if (loading) return null;
  if (!data || data.unlocked_count === 0) return null;

  const unlocked = data.milestones.filter(m => m.unlocked);
  const nextUp = data.milestones.find(m => !m.unlocked);

  return (
    <>
      {/* Celebration Modal */}
      <AnimatePresence>
        {celebrating && (
          <CelebrationModal
            milestone={celebrating}
            onClose={handleCloseCelebration}
            onShare={handleShare}
          />
        )}
      </AnimatePresence>

      {/* Dashboard Widget */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl border border-border overflow-hidden"
        data-testid="milestones-widget"
      >
        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-sm text-[#1A1A1A]">Milestones</h3>
                <p className="text-xs text-muted-foreground">{data.unlocked_count}/{data.total_count} unlocked</p>
              </div>
            </div>
            {/* Progress ring */}
            <div className="relative w-10 h-10">
              <svg className="w-10 h-10 -rotate-90" viewBox="0 0 36 36">
                <circle cx="18" cy="18" r="14" fill="none" stroke="#E5E7EB" strokeWidth="3" />
                <circle
                  cx="18" cy="18" r="14" fill="none" stroke="#F59E0B" strokeWidth="3"
                  strokeDasharray={`${(data.unlocked_count / data.total_count) * 88} 88`}
                  strokeLinecap="round"
                />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-amber-700">
                {Math.round((data.unlocked_count / data.total_count) * 100)}%
              </span>
            </div>
          </div>

          {/* Unlocked badges grid */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {unlocked.slice(0, 8).map(m => {
              const Icon = ICON_MAP[m.icon] || Award;
              return (
                <div
                  key={m.id}
                  className="flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 border border-amber-200"
                  title={m.title}
                >
                  <Icon className="w-3 h-3 text-amber-600" />
                  <span className="text-[10px] font-medium text-amber-700">{m.title.split(' ')[0]}</span>
                </div>
              );
            })}
            {unlocked.length > 8 && (
              <span className="text-[10px] text-muted-foreground self-center">+{unlocked.length - 8} more</span>
            )}
          </div>

          {/* Next milestone */}
          {nextUp && (
            <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-100">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                  {(() => { const NI = ICON_MAP[nextUp.icon] || Award; return <NI className="w-3 h-3 text-gray-400" />; })()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-muted-foreground">Next milestone</p>
                  <p className="text-xs font-medium text-[#1A1A1A] truncate">{nextUp.title} — {nextUp.desc}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Share CTA */}
        <div className="border-t border-border">
          <button
            onClick={handleShare}
            className="w-full px-5 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
            data-testid="milestones-share-btn"
          >
            <span className="text-sm font-medium text-primary flex items-center gap-2">
              <Share2 className="w-4 h-4" /> Share Achievements
            </span>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      </motion.div>

      {/* Share Modal */}
      <AnimatePresence>
        {showShareModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowShareModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
              data-testid="milestones-share-modal"
            >
              <div className="p-5 border-b border-border flex items-center justify-between">
                <h3 className="font-semibold text-[#1A1A1A]">Share Achievements</h3>
                <button onClick={() => setShowShareModal(false)} className="text-muted-foreground hover:text-[#1A1A1A]">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5">
                {!shareData ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : (
                  <>
                    <MilestonesCardPreview data={shareData} />
                    <div className="mt-5 space-y-2">
                      <p className="text-xs text-muted-foreground mb-2 text-center">Share via</p>
                      <div className="grid grid-cols-2 gap-2">
                        <button onClick={handleShareWhatsApp} className="flex items-center justify-center gap-2 p-3 rounded-xl bg-green-50 text-green-700 hover:bg-green-100 transition-colors text-sm font-medium" data-testid="milestones-share-whatsapp">
                          <MessageCircle className="w-4 h-4" /> WhatsApp
                        </button>
                        <button onClick={handleShareTwitter} className="flex items-center justify-center gap-2 p-3 rounded-xl bg-sky-50 text-sky-700 hover:bg-sky-100 transition-colors text-sm font-medium" data-testid="milestones-share-twitter">
                          <Twitter className="w-4 h-4" /> X / Twitter
                        </button>
                      </div>
                      <button onClick={handleCopyLink} className="w-full flex items-center justify-center gap-2 p-3 rounded-xl bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors text-sm font-medium" data-testid="milestones-share-copy">
                        {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                        {copied ? 'Copied!' : 'Copy Referral Link'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
